/**
 * Quarterly, as values.
 *
 * Every page had grown its own literals — eleven different ideas of a radius, a
 * label size and a row height — which is why restyling a page at a time never
 * changed how the product felt. Nothing in `theme.ts` names a colour or a
 * distance of its own; it all comes from here, so the design is a set of numbers
 * in one file rather than a habit spread over eleven.
 *
 * The values are the designer's. Where the artboards disagreed with each other
 * — and they did, on a row height, a card radius, a field and a chip — the
 * design system's `DS-Rulings` sheet names the single value that wins, and that
 * is the column this file is built to. Where it is silent, the product's own
 * value stands with a note saying so, rather than a number invented to fill the
 * gap.
 */

export const TONES = ['property', 'equity', 'crypto', 'pension', 'cash', 'liability'] as const
export type Tone = (typeof TONES)[number]

/** Everything that differs between light and dark. */
export interface Palette {
  ground: string
  card: string
  /** A panel inside a card — the chart's own ground. */
  inner: string
  hairline: string
  /** The lighter rule between two rows of a table. */
  rowRule: string
  /**
   * The dashed edge of the way in from an empty page. Darker than the hairline
   * on purpose: it is the only border in the system that has to be seen from
   * across the card rather than merely separate two things.
   */
  dashedEdge: string
  text: string
  /** Body prose, a step darker than muted. */
  body: string
  muted: string
  /** The faintest text there is: axis labels, counts, footnotes. */
  faint: string
  accent: string
  accentHover: string
  accentInk: string
  gain: string
  gainWash: string
  loss: string
  lossEdge: string
  /** The pill nav's selected fill and the range control's trough. */
  selected: string
  hover: string
  focus: string
  /** The 4px ring the focused field wears, and every other focusable thing. */
  focusRing: string
  scrim: string
  /** The panel that says what a figure is about to become. */
  preview: string
  previewInk: string
  /**
   * A monogram tile takes the colour of what it stands for. Six pairs, because
   * a property, a fund, a coin, a pension, a bank balance and a debt are six
   * different kinds of thing and the list is read down the left edge.
   */
  tones: Readonly<Record<Tone, readonly [string, string]>>
  warning: string
  warningWash: string
  critical: string
  avatar: string
  avatarInk: string
  /** The stacked bar and the chart, five steps of the accent. */
  series: readonly [string, string, string, string, string, string, string]
  /** `DS-Space`'s shadow-card: every card, barely there on purpose. */
  shadowCard: string
  /** `DS-Space`'s shadow-pop: the selected segment, and a tooltip. */
  shadowPop: string
  /**
   * `DS-Space`'s shadow-menu. Its elevation table is stated in light, so dark
   * keeps the opacity it already had and takes the light geometry, which is
   * what every other shadow in this palette does.
   */
  shadowMenu: string
  /**
   * How much of the accent the first series is filled with under its line. Two
   * numbers rather than one because a wash that reads at 8 % on paper-white
   * disappears on the dark ground.
   */
  chartFill: string
}

export const LIGHT: Palette = {
  ground: '#F4F5F3',
  card: '#FFFFFF',
  inner: '#FBFCFB',
  hairline: '#E2E4E0',
  rowRule: '#EDEFEC',
  dashedEdge: '#B9C6C1',
  text: '#14181A',
  body: '#3E4644',
  muted: '#5A6261',
  faint: '#8B9391',
  accent: '#125E4F',
  accentHover: '#0C463B',
  accentInk: '#FFFFFF',
  gain: '#146A4F',
  gainWash: '#E3F0EA',
  loss: '#9C3B2C',
  lossEdge: '#E8C6BF',
  selected: '#EDEFEC',
  hover: '#F0F2EF',
  focus: '#125E4F',
  focusRing: 'rgba(18, 94, 79, .10)',
  scrim: 'rgba(20, 24, 26, .42)',
  preview: '#EAF2EE',
  previewInk: '#4A5654',
  tones: {
    property: ['#EAF2EE', '#125E4F'],
    equity: ['#E9EFF3', '#2F5E7A'],
    crypto: ['#F5EEE3', '#8A5A12'],
    pension: ['#EEEDF5', '#4A4480'],
    cash: ['#ECEEEB', '#5A6261'],
    liability: ['#F7EAE7', '#9C3B2C'],
  },
  warning: '#8A5A12',
  warningWash: '#F6EEDC',
  critical: '#9C3B2C',
  avatar: '#DDE6E2',
  avatarInk: '#125E4F',
  series: ['#125E4F', '#3E8C77', '#7FB3A3', '#A8C2B9', '#CFD8D4', '#C97F6E', '#8B9391'],
  shadowCard: '0 1px 2px rgba(20, 24, 26, .04)',
  shadowPop: '0 2px 6px rgba(20, 24, 26, .08)',
  shadowMenu: '0 8px 24px rgba(20, 24, 26, .14)',
  chartFill: '.08',
}

/**
 * Dark is given as eight values in the foundations — ground, card, hairline,
 * text, muted, accent, gain, loss. The rest are stated here rather than pretended
 * to be the designer's: a row rule a shade off the card, a body a step under the
 * text, and the series stepped the same way between accent and hairline.
 */
export const DARK: Palette = {
  ground: '#101413',
  card: '#171C1B',
  inner: '#141918',
  hairline: '#262D2B',
  rowRule: '#1F2624',
  // Stated rather than the designer's: no mockup draws a dashed edge in dark.
  // The accent mixed a fifth of the way into the hairline, which is the
  // relationship the light pair has.
  dashedEdge: '#344B44',
  text: '#EDF0EE',
  body: '#C3CAC8',
  muted: '#97A09E',
  faint: '#7C8583',
  accent: '#6FC3A9',
  accentHover: '#8ED3BD',
  accentInk: '#0B1210',
  gain: '#7FC7A4',
  gainWash: 'rgba(127, 199, 164, .16)',
  loss: '#E0897A',
  lossEdge: 'rgba(224, 137, 122, .42)',
  selected: '#222827',
  hover: '#1C2321',
  focus: '#6FC3A9',
  focusRing: 'rgba(111, 195, 169, .16)',
  scrim: 'rgba(6, 9, 8, .58)',
  preview: '#18241F',
  previewInk: '#A8B4B0',
  tones: {
    property: ['#24312C', '#7FC7A4'],
    equity: ['#222C33', '#8FB6CC'],
    crypto: ['#332C22', '#D9A97A'],
    pension: ['#2A2833', '#A9A3D1'],
    cash: ['#252B2A', '#B6C0BD'],
    liability: ['#332525', '#E0897A'],
  },
  warning: '#D2B05A',
  warningWash: 'rgba(210, 176, 90, .16)',
  critical: '#E0897A',
  avatar: '#22332D',
  avatarInk: '#6FC3A9',
  series: ['#6FC3A9', '#4E9C85', '#3B7666', '#2E574C', '#3A4340', '#C97F6E', '#97A09E'],
  shadowCard: '0 1px 2px rgba(0, 0, 0, .4)',
  shadowPop: '0 2px 6px rgba(0, 0, 0, .5)',
  shadowMenu: '0 8px 24px rgba(0, 0, 0, .8)',
  chartFill: '.12',
}

const FONT = `'Schibsted Grotesk', ui-sans-serif, system-ui, -apple-system, sans-serif`

export const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400;500;600;700;800&display=swap'

/** The type scale, the 4px spacing root, the radii and the chart's weights. */
const SHAPE = [
  `--nw-font: ${FONT};`,
  // `DS-Type` gives the mono stack for addresses, keys and IDs. It was being
  // read by the connect dialog's textarea before it was declared anywhere, so
  // the only thing holding that font up was the fallback inside the `var()`.
  `--nw-font-mono: ui-monospace, Menlo, monospace;`,

  // `DS-Type`'s scale, under the names this product already reads: display is
  // its display-xl, display-lg the same figure at phone width.
  `--nw-text-display: 46px;`,
  `--nw-text-display-leading: 1;`,
  `--nw-text-display-lg: 34px;`,
  `--nw-text-page: 26px;`,
  `--nw-text-stat: 27px;`,
  `--nw-text-figure: 24px;`,
  `--nw-text-figure-leading: 30px;`,
  `--nw-text-title: 15px;`,
  `--nw-text-title-leading: 20px;`,
  `--nw-text-body: 14px;`,
  `--nw-text-body-leading: 21px;`,
  `--nw-text-cell: 13px;`,
  `--nw-text-cell-leading: 18px;`,
  `--nw-text-meta: 11px;`,
  `--nw-text-meta-leading: 16px;`,
  `--nw-text-control: 13px;`,
  // Named after the chip and no longer the chip's: some forty-five rules read
  // it for notes, ledes, small print and uppercase labels. It is the 12px step,
  // and a reader who takes the name for the meaning will mis-file all of them.
  `--nw-text-chip: 12px;`,
  // The text inside a box somebody types in, which is not a control's label and
  // not a title. `--nw-text-control` is the word on a button; `--nw-text-title`
  // is a card head that happens to share this figure. Without a name of its own
  // a field had to borrow one of those, and the borrowing reads as conformance.
  // The sign-in field is 16 and not this, for a reason that is not about type:
  // see `.nw-auth-field input` in `Login.tsx`.
  `--nw-text-field: 15px;`,
  // The product's name, which is set twice and reused nowhere: once in the app
  // bar and once on the way in. Its neighbours are 15 and 24 and it is a
  // rounding of neither — it is named so that a sweep of the literals cannot
  // mistake it for a title that drifted, and correct it onto a step it was
  // never on.
  `--nw-text-wordmark: 17px;`,

  `--nw-weight-body: 400;`,
  `--nw-weight-medium: 500;`,
  `--nw-weight-strong: 600;`,
  `--nw-weight-figure: 700;`,

  `--nw-track-display: -.03em;`,
  `--nw-track-page: -.025em;`,
  // The product name's own, read by both places it appears so they cannot track
  // the same word two ways. Not `--nw-track-figure` although it shares the value:
  // a name is not a number, and borrowing the figure's token would leave the
  // wordmark reading a rule about digits, which is how a value comes to move under it.
  `--nw-track-wordmark: -.02em;`,
  `--nw-track-figure: -.02em;`,
  `--nw-track-title: -.01em;`,
  `--nw-track-label: .06em;`,
  // The ore steps down to half the krone beside it and takes the muted ink.
  `--nw-text-ore: .5em;`,

  // `DS-Space`'s scale, by its own step numbers. Steps 5 and 7 arrived with the
  // design system and the two that were here under those names were 24 and 32,
  // which are its 6 and 8 — every reader was moved rather than every value.
  `--nw-space-1: 4px;`,
  `--nw-space-2: 8px;`,
  `--nw-space-3: 12px;`,
  `--nw-space-4: 16px;`,
  `--nw-space-5: 20px;`,
  `--nw-space-6: 24px;`,
  `--nw-space-7: 28px;`,
  `--nw-space-8: 32px;`,
  `--nw-space-12: 48px;`,
  `--nw-page-pad-x: var(--nw-space-8);`,
  `--nw-page-pad-y: var(--nw-space-7);`,
  `--nw-stack: var(--nw-space-6);`,
  // Between cards, both directions. Its own name rather than the stack above,
  // which also sets the rhythm of headings and notes the design system has not
  // ruled on.
  `--nw-card-gap: var(--nw-space-5);`,
  `--nw-page-max: 1280px;`,
  // 1280 - 64 page padding - 20 gap = 1196, split 840 + 356. At 320 the main
  // column lands on 876 and the chart's plot area stops being a round number,
  // which is the whole reason this is not a round-looking number itself.
  `--nw-sidebar-w: 356px;`,

  `--nw-radius-sm: 10px;`,
  `--nw-radius-md: 12px;`,
  // `DS-Space`'s radius table has no 14, but `DS-Controls` draws the menu at
  // one. Kept at the value both the menu and the panels already use rather than
  // rounded into a neighbouring step that neither sheet asks for.
  `--nw-radius-panel: 14px;`,
  `--nw-radius-lg: 16px;`,
  `--nw-radius-xl: 20px;`,
  `--nw-radius-full: 999px;`,
  `--nw-hairline-width: 1px;`,

  `--nw-card-pad-y: 22px;`,
  `--nw-card-pad-x: 26px;`,
  // The heights `DS-Space` names, for the components that read one. A row never
  // sizes itself from its content, so a table that grew a taller cell is a bug
  // in the cell rather than a new row height.
  `--nw-h-table-row: 44px;`,
  `--nw-h-table-row-compact: 36px;`,
  `--nw-h-table-header: 36px;`,
  `--nw-h-table-total: 52px;`,
  `--nw-h-tab-strip: 44px;`,
  `--nw-h-chip: 24px;`,
  // The one chip that is a card's whole figure, which `DS-Rulings` does not
  // cover: the 24 above is the chip that stands beside text, and beside a
  // figure it would not read as the headline it is. Carried across rather than
  // chosen — the 24 chip sits on the 21px line of the text next to it, eight
  // sevenths of it, and a figure is 24px type on a 30px line, so the same eight
  // sevenths is 34,29. Held at 34 because that is what the line box of a plain
  // figure in the same card already measures, which leaves a card whose
  // headline is a chip exactly as tall as one whose headline is a number.
  `--nw-h-chip-figure: 34px;`,
  `--nw-h-nav-bar: 68px;`,
  // Every tap target at least 44px tall, phone and desktop alike. A control may
  // be drawn shorter and still be a 44 target — the segmented item is 38 and
  // takes the rest from a pseudo-element.
  `--nw-tap-min: 44px;`,
  // `DS-Rulings` v1.1 rules 40 / 44 / 52 for free-standing controls: anything a
  // person tabs to as a discrete target. A secondary button read 38 because it
  // had been derived from the segmented item, and that borrowing is precisely
  // what the rule now forbids — two names rather than one, so it cannot recur.
  `--nw-h-control-sm: 40px;`,
  `--nw-h-control-md: 44px;`,
  // A square button holding nothing but a glyph, which the scale above does not
  // govern: it is not read as a control among controls but as a target beside
  // the thing it acts on. Its own name so that the avatar it happens to match
  // cannot drag it, which is how the two got coupled before.
  `--nw-h-icon-btn: 32px;`,
  // Derived from the track it sits in rather than chosen from the scale above,
  // which is why it is arithmetic here instead of a fourth step: no
  // free-standing control may read it.
  `--nw-segmented-inset: 3px;`,
  `--nw-h-segmented-item: calc(var(--nw-h-control-md) - 2 * var(--nw-segmented-inset));`,
  `--nw-h-avatar-sm: 24px;`,
  `--nw-h-avatar-md: 32px;`,
  `--nw-chip-pad-x: 10px;`,
  // The focused edge and the ring around it, as values rather than as two
  // numbers repeated wherever a component has to draw both at once.
  `--nw-focus-edge: 2px;`,
  `--nw-focus-ring-w: 4px;`,
  `--nw-bar-h: 12px;`,
  `--nw-plot-height: 244px;`,

  `--nw-chart-line: 2.5px;`,
  `--nw-chart-cap: round;`,
  `--nw-chart-dot: 7px;`,
  `--nw-chart-grid-width: 1px;`,

  // The modal, specified to the pixel in C-AddHolding.
  `--nw-modal-w: 576px;`,
  `--nw-modal-wide-w: 920px;`,
  `--nw-radius-xl: 20px;`,
  `--nw-shadow-dialog: 0 24px 60px rgba(20, 24, 26, .24);`,
  `--nw-field-h: var(--nw-h-control-md);`,
  `--nw-field-radius: 12px;`,
  // A dialog's footer button is a primary action, and the primary is 44. The
  // scale did not answer this one on its own, so it was left at 46 until Lars
  // ruled; it is not a free choice to reopen.
  `--nw-footer-btn-h: var(--nw-h-control-md);`,
  `--nw-h-table-total: 52px;`,

  // The way in, specified to the pixel in C-SignIn, C-SignUp, C-SignInPasskey
  // and C-AuthPhone. Its field is taller than the modal's and its column is held
  // at 640 whatever the window does, so neither can be read off the values above.
  `--nw-auth-col: 640px;`,
  `--nw-h-control-lg: 52px;`,
  `--nw-auth-field-h-phone: 54px;`,
  `--nw-auth-pad-x: 128px;`,
  `--nw-auth-pitch-pad-x: 96px;`,
  `--nw-auth-edge: 48px;`,
  `--nw-auth-edge-top: 44px;`,
  `--nw-auth-edge-bottom: 38px;`,
  `--nw-auth-phone-gutter: 24px;`,
  `--nw-auth-phone-top: 46px;`,
  `--nw-auth-title: 32px;`,
  `--nw-auth-title-phone: 28px;`,
  `--nw-auth-lede: 15px;`,
  `--nw-auth-pitch-title: 30px;`,
  `--nw-auth-mark: 64px;`,
  `--nw-auth-bar-h: 5px;`,
].join(' ')

/**
 * The panel beside the form.
 *
 * Stated once rather than as a light pair and a dark one: it is the brand's own
 * surface in all four mockups and keeps that colour under either scheme, so the
 * ink that has to read on it is fixed here instead of tracking a palette that
 * moves out from under it.
 */
/**
 * Where the panel stops being a column beside the form and becomes a card under
 * it. A number rather than a custom property because a media query cannot read
 * one, and it belongs here with the rest of the measurements all the same.
 */
export const AUTH_NARROW_PX = 900

const PITCH = [
  `--nw-pitch: #0E4034;`,
  `--nw-pitch-card: #14503F;`,
  `--nw-pitch-edge: #1E6450;`,
  `--nw-pitch-ink: #E8F0EC;`,
  `--nw-pitch-body: #A9C6BB;`,
  `--nw-pitch-mark: #A7E0C6;`,
].join(' ')

function paletteVars(palette: Palette, scheme: 'light' | 'dark'): string {
  return [
    `--nw-ground: ${palette.ground};`,
    `--nw-card: ${palette.card};`,
    `--nw-inner: ${palette.inner};`,
    `--nw-hairline: ${palette.hairline};`,
    `--nw-row-rule: ${palette.rowRule};`,
    `--nw-dashed-edge: ${palette.dashedEdge};`,
    `--nw-text: ${palette.text};`,
    `--nw-body-ink: ${palette.body};`,
    `--nw-muted: ${palette.muted};`,
    `--nw-faint: ${palette.faint};`,
    `--nw-accent: ${palette.accent};`,
    `--nw-accent-hover: ${palette.accentHover};`,
    `--nw-accent-ink: ${palette.accentInk};`,
    `--nw-gain: ${palette.gain};`,
    `--nw-gain-wash: ${palette.gainWash};`,
    `--nw-loss: ${palette.loss};`,
    `--nw-loss-edge: ${palette.lossEdge};`,
    `--nw-selected: ${palette.selected};`,
    `--nw-hover: ${palette.hover};`,
    `--nw-focus: ${palette.focus};`,
    `--nw-focus-ring: ${palette.focusRing};`,
    `--nw-scrim: ${palette.scrim};`,
    `--nw-preview: ${palette.preview};`,
    `--nw-preview-ink: ${palette.previewInk};`,
    ...TONES.flatMap((tone) => [
      `--nw-tone-${tone}: ${palette.tones[tone][0]};`,
      `--nw-tone-${tone}-ink: ${palette.tones[tone][1]};`,
    ]),
    `--nw-warning: ${palette.warning};`,
    `--nw-warning-wash: ${palette.warningWash};`,
    `--nw-critical: ${palette.critical};`,
    // The one bar that has to be read whatever the theme is, so its ink is fixed
    // rather than tracking a palette that moves out from under it.
    `--nw-alarm: color-mix(in srgb, ${palette.critical} 62%, #000);`,
    `--nw-alarm-ink: #FFFFFF;`,
    `--nw-avatar: ${palette.avatar};`,
    `--nw-avatar-ink: ${palette.avatarInk};`,
    ...palette.series.map((colour, index) => `--nw-series-${index + 1}: ${colour};`),
    `--nw-series-other: ${palette.faint};`,
    `--nw-shadow-card: ${palette.shadowCard};`,
    `--nw-shadow-pop: ${palette.shadowPop};`,
    `--nw-shadow-menu: ${palette.shadowMenu};`,
    `--nw-chart-fill: ${palette.chartFill};`,
    `color-scheme: ${scheme};`,
    // The rest of the product still reads the names it always has; they are
    // aliases now rather than a second palette that could drift from this one.
    `--nw-plane: ${palette.ground};`,
    `--nw-surface: ${palette.card};`,
    `--nw-ink: ${palette.text};`,
    `--nw-ink-2: ${palette.muted};`,
    `--nw-grid: ${palette.rowRule};`,
    `--nw-axis: ${palette.hairline};`,
    `--nw-serious: ${palette.warning};`,
  ].join(' ')
}

export function tokenCss(): string {
  return [
    `:root { ${SHAPE} ${PITCH} ${paletteVars(LIGHT, 'light')} }`,
    `@media (prefers-color-scheme: dark) { :root:not([data-theme='light']) { ${paletteVars(DARK, 'dark')} } }`,
    `:root[data-theme='dark'] { ${paletteVars(DARK, 'dark')} }`,
  ].join('\n')
}
